import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, GeplanteFaelligkeiten as Sicht } from "@kundeninfo/api-client";
import { formatBetrag, formatDatum } from "../format.js";
import { apiMeldung } from "./vorgangsdetail.js";
import { Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "./meHuellen.js";
import "./GeplanteFaelligkeiten.css";

/**
 * DER LESEKASTEN „Geplante Fälligkeiten" am Auftragsdetail (Spec, „Oberfläche").
 *
 * **Er ist LESEND.** Der einzige Knopf rechnet die Planung NEU (der rollierende
 * Horizont eines unbefristeten Auftrags wandert mit jedem Tag, Festlegung Z8) — er
 * ändert nichts am Auftrag und schreibt keine Belegversion fort. Gepflegt wird der
 * Zahlplan im Modal.
 *
 * **Die REIHENFOLGE ist eine Abwägung** (Festlegung Z7): Die Hausregel „Schlechtestes
 * oben" trifft hier auf einen KALENDER. Die Jahre stehen deshalb AUFSTEIGEND — ein
 * rückwärts gelesener Kalender ist unlesbar —, und die Hausregel wirkt zweifach an
 * anderer Stelle: Der Kastenkopf nennt ZUERST die Zahl der überfälligen Posten über
 * alle Jahre, und innerhalb jedes Jahres stehen überfällige Posten vorn (der Server
 * liefert sie bereits in dieser Ordnung).
 *
 * **Eigener CSS-Block `gfk__`.**
 */

export type FaelligkeitenClient =
  Pick<ApiClient, "getFaelligkeiten" | "berechneFaelligkeiten">;

export type GeplanteFaelligkeitenProps = {
  client: FaelligkeitenClient;
  customerId: string;
  /** Ein Wechsel setzt ALLES zurück und lädt neu. */
  orderId: string;
  /** Die Währung des Auftrags — für die Betragsformatierung. Vorgabe „EUR". */
  currency?: string;
  /**
   * Ob der Knopf „Neu berechnen" überhaupt erscheint — Vorgabe `true`.
   *
   * An einem stornierten oder abgeschlossenen Auftrag lehnt der Server die Neuberechnung
   * mit 422 `AUFTRAG_NICHT_AKTIV` ab (Aufgabe 4): Es entstehen dort keine weiteren
   * Leistungszeiträume mehr. `AuftragDetail` reicht deshalb `auftrag.status === "ACTIVE"`
   * herein — derselbe Weg wie bei den übrigen Knöpfen, die an einem erledigten Vorgang
   * verschwinden (Bearbeiten in `AngebotDetail`, „Rechnung vorschlagen" hier nebenan): ein
   * Knopf, der sicher abgewiesen wird, ist keine Bedienführung, sondern eine Falle.
   */
  neuBerechnenErlaubt?: boolean;
};

/**
 * Die Bezeichnung EINES Leistungsjahres — die Jahreszahl beim Kalenderjahr, der
 * Zeitraum beim Vertragsjahr.
 *
 * „Leistungsjahr 2026" über einem Vertragsjahr vom 15.03.2026 bis 14.03.2027 ist
 * falsch: Die Hälfte seiner Posten fällt in 2027, und wer die Liste überfliegt, sucht
 * sie im nächsten Block. Beim KALENDERJAHR bleibt die Jahreszahl — dort ist sie die
 * genauere Angabe, nicht die kürzere.
 *
 * Sie steht an ZWEI Stellen (Überschrift und Summenzeile) und entsteht deshalb hier
 * EINMAL (Festlegung K8): Eine Summenzeile „Summe 2026" unter der Überschrift
 * „Leistungsjahr 15.03.2026 – 14.03.2027" wäre derselbe Irrtum eine Zeile tiefer.
 */
function jahresBezeichnung(
  periodBasis: Sicht["periodBasis"],
  jahr: { periodStart: string; periodEnd: string },
): string {
  if (periodBasis === "CALENDAR_YEAR") return jahr.periodStart.slice(0, 4);
  return `${formatDatum(jahr.periodStart)} – ${formatDatum(jahr.periodEnd)}`;
}

export function GeplanteFaelligkeiten(
  props: GeplanteFaelligkeitenProps,
): ReactElement {
  const { client, customerId, orderId, currency = "EUR", neuBerechnenErlaubt = true } = props;
  const [sicht, setSicht] = useState<Sicht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [rechnet, setRechnet] = useState(false);
  const lauf = useRef(0);
  const getFaelligkeiten = client.getFaelligkeiten;

  useEffect(() => {
    const meiner = ++lauf.current;
    setSicht(null);
    setFehler(null);
    getFaelligkeiten(customerId, orderId)
      .then((antwort) => {
        if (lauf.current !== meiner) return;
        setSicht(antwort);
      })
      .catch(() => {
        if (lauf.current !== meiner) return;
        setFehler("Die geplanten Fälligkeiten konnten nicht geladen werden.");
      });
    return () => { lauf.current++; };
  }, [getFaelligkeiten, customerId, orderId]);

  async function neuBerechnen(): Promise<void> {
    setRechnet(true);
    setFehler(null);
    try {
      setSicht(await client.berechneFaelligkeiten(customerId, orderId));
    } catch (fehlerAntwort) {
      // NUR die Meldung, NIE die Liste: Ein stornierter oder abgeschlossener Auftrag
      // (422 AUFTRAG_NICHT_AKTIV) darf die schon geladene Tabelle nicht wegwischen — der
      // Kasten bleibt lesbar, die Meldung steht als Band darüber (Gesamtreview, I1).
      setFehler(apiMeldung(
        fehlerAntwort, "Die geplanten Fälligkeiten konnten nicht neu gerechnet werden."));
    } finally {
      setRechnet(false);
    }
  }

  if (sicht === null) {
    if (fehler !== null) return <Hinweis art="fehler">{fehler}</Hinweis>;
    return <p className="gfk__laedt">Geplante Fälligkeiten werden geladen …</p>;
  }

  const ueberfaellig = sicht.anzahlUeberfaellig;
  return (
    <section className="gfk" aria-label="Geplante Fälligkeiten">
      <div className="gfk__kopf">
        <h3 className="gfk__titel">Geplante Fälligkeiten</h3>
        {/* SCHLECHTESTES OBEN: der Befund steht vor jeder Tabelle. */}
        {ueberfaellig > 0 && (
          <Hinweis art="warnung" className="gfk__ueberfaellig">
            {ueberfaellig === 1
              ? "1 überfälliger Posten"
              : `${ueberfaellig} überfällige Posten`}
          </Hinweis>
        )}
        {neuBerechnenErlaubt && (
          <KiKnopf className="gfk__sekundaer" deaktiviert={rechnet}
            onClick={() => { void neuBerechnen(); }}>
            Neu berechnen
          </KiKnopf>
        )}
      </div>

      {/* Das BAND einer fehlgeschlagenen Neuberechnung — ÜBER der Liste, nicht an ihrer
          Stelle: Die zuletzt geladene Tabelle bleibt stehen (Gesamtreview, I1). */}
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}

      {!sicht.hatLaufzeit && (
        <p className="gfk__hinweis">
          Ohne Laufzeitbeginn entstehen keine Leistungszeiträume — und damit keine
          geplanten Fälligkeiten. Laufzeitbeginn im Auftrag nachtragen.
        </p>
      )}
      {sicht.hatLaufzeit && !sicht.hatZahlplan && (
        <p className="gfk__hinweis">
          Für diesen Auftrag ist keine Zahlungsvereinbarung hinterlegt. Sie wird im
          Auftrag unter „Zahlungsvereinbarung" gepflegt.
        </p>
      )}

      {sicht.jahre.map((jahr) => (
        <div className="gfk__jahr" key={jahr.periodStart}>
          <h4 className="gfk__jahrTitel">
            Leistungsjahr {jahresBezeichnung(sicht.periodBasis, jahr)}
          </h4>
          {/* me-tabelle (ME-Etappe 3, Aufgabe 4): Rahmen, Zebra, Fusszeile, Rollen; der
              Planbetrag rechtsbündig. */}
          <Tabelle>
            <table className="gfk__tabelle">
              <thead>
                <tr>
                  <th scope="col">Fälligkeit</th>
                  <th scope="col">Rate</th>
                  <th scope="col">Teilzeitraum</th>
                  <th scope="col">Bemessung</th>
                  <th scope="col" data-ausrichtung="rechts">Planbetrag</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {jahr.posten.map((posten) => (
                  <tr key={posten.id}
                    className={posten.ueberfaellig ? "gfk__zeile--ueberfaellig" : undefined}>
                    <td>{formatDatum(posten.dueDate)}</td>
                    <td>{posten.lineLabel ?? "—"}</td>
                    <td>
                      {formatDatum(posten.subperiodStart)} –
                      {" "}{formatDatum(posten.subperiodEnd)}
                    </td>
                    <td>
                      {posten.measurementDate === null
                        ? "Planmenge"
                        : `Istmenge zum ${formatDatum(posten.measurementDate)}`}
                      {posten.plannedQuantity === null
                        ? "" : ` (${posten.plannedQuantity})`}
                    </td>
                    <td className="ki-num" data-ausrichtung="rechts">
                      {formatBetrag(Number(posten.plannedAmount), currency)}
                    </td>
                    <td>{STATUS_TEXT[posten.status]}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={4}>
                    Summe {jahresBezeichnung(sicht.periodBasis, jahr)}
                  </th>
                  <td className="ki-num" data-ausrichtung="rechts">{formatBetrag(Number(jahr.summe), currency)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </Tabelle>
        </div>
      ))}

      {sicht.jahre.length > 0 && (
        <p className="gfk__fuss">
          Gesamt: <strong>{formatBetrag(Number(sicht.summeGesamt), currency)}</strong>
          {sicht.horizontBis !== null
            && ` — geplant bis ${formatDatum(sicht.horizontBis)}`}
        </p>
      )}
    </section>
  );
}

const STATUS_TEXT: Record<string, string> = {
  PLANNED: "Geplant", INVOICED: "Abgerechnet", CANCELLED: "Entfallen",
};
